/**
 * `/account/settings/notifications`. R1 and R5: the route the email already
 * links to, behind the signed-in guard, reading
 * `/notification-settings/get-global` once and composing the preset picker,
 * the account-wide email kill switch, and the alert group list.
 *
 * The file is named `account_.settings.notifications` so this route stays
 * out of both `/account`'s and `/settings`'s trees (neither renders an
 * `Outlet`), the same reason `account_.edit.tsx` and `settings_.sessions.tsx`
 * give for their own underscores.
 *
 * `killSwitchSaved` is local UI state, not a second copy of server data --
 * the checkbox's own `checked` value still comes from `state`, read fresh
 * after every refetch. It only tracks "did the last kill-switch change
 * succeed," the same one-slot pattern `PrivacyCard.tsx` already uses for its
 * own `SavedNote`, added so `error-modals`'s always-on rule (a successful
 * save is announced with `SavedNote`, never a toast) is actually satisfied
 * here.
 *
 * The quiet-hours card reads its own query so a slow quiet-hours response
 * never blocks the rest of the screen. The zone is captured from the browser
 * at save time rather than read from the profile.
 */
import {
  useNotificationSettingsGetGlobal,
  useNotificationSettingsGetQuietHours,
  useNotificationSettingsSetEmailKillSwitch,
} from "@sportsmet/notification-sdk/private";
import { Button, Card, Checkbox, SavedNote } from "@sportsmet/ui";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { extractErrorCode, showApiError } from "../errors/apiErrorBus";
import { SettingsBackLink } from "../features/account/SettingsBackLink";
import { AlertGroupList } from "../features/notifications/AlertGroupList";
import { resolveNotificationSettingsState } from "../features/notifications/notificationSettingsState";
import { PresetPicker } from "../features/notifications/PresetPicker";
import { QuietHoursCard } from "../features/notifications/QuietHoursCard";
import { RoutePendingScreen } from "../shell/RoutePendingScreen";

const NotificationSettingsScreen = (): ReactNode => {
  const settings = useNotificationSettingsGetGlobal({});
  const quietHours = useNotificationSettingsGetQuietHours({});

  useEffect(() => {
    const code = extractErrorCode(settings.data);
    if (code !== null)
      showApiError(settings.data, undefined, settings.dataUpdatedAt);
  }, [settings.dataUpdatedAt]);

  useEffect(() => {
    const code = extractErrorCode(quietHours.data);
    if (code !== null)
      showApiError(quietHours.data, undefined, quietHours.dataUpdatedAt);
  }, [quietHours.dataUpdatedAt]);

  const [killSwitchSaved, setKillSwitchSaved] = useState(false);

  const killSwitch = useNotificationSettingsSetEmailKillSwitch({
    onSuccess: (result) => {
      if (!showApiError(result)) setKillSwitchSaved(true);
      void settings.refetch();
    },
  });

  if (settings.isPending) return <RoutePendingScreen />;

  const data = settings.data?.data ?? null;
  const state = resolveNotificationSettingsState({
    isLoading: settings.isPending,
    hasFailed: settings.isError || Boolean(settings.data.error),
    errorCode: settings.data?.error?.code ?? null,
    alerts: data?.alerts ?? null,
    isEmailKillSwitchOn: data?.isEmailKillSwitchOn ?? false,
  });

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4">
      <SettingsBackLink />
      <div className="flex flex-col gap-1">
        <h1 className="text-h1 text-ink">Notification settings</h1>
        <p className="text-body text-ink-secondary">
          Pick how Sportsmet reaches you. Changes save as you make them.
        </p>
      </div>
      {state.phase === "failed" ? (
        <div data-testid="notification-settings-failed">
          <Card>
            <h2 className="text-h2 text-ink">
              We could not load your notification settings
            </h2>
            <p className="text-body text-ink-secondary">
              Check your connection and try again.
            </p>
            <Button
              variant="secondary"
              onClick={() => {
                void settings.refetch();
              }}
            >
              Try again
            </Button>
          </Card>
        </div>
      ) : (
        <>
          <PresetPicker
            presets={data?.presets ?? []}
            activePresetId={data?.activePresetId ?? null}
            onApplied={() => {
              void settings.refetch();
            }}
          />
          <Card>
            <div data-testid="email-kill-switch">
              <Checkbox
                label="Turn off all email"
                description="You won't get email for anything, anywhere, until you turn this back on."
                checked={
                  state.phase === "ready" ? state.isEmailKillSwitchOn : false
                }
                disabled={killSwitch.isPending}
                onCheckedChange={(next) => {
                  setKillSwitchSaved(false);
                  killSwitch.mutate({ isEnabled: next === true });
                }}
              />
              <SavedNote saved={killSwitchSaved} />
            </div>
          </Card>
          {quietHours.data?.data ? (
            <QuietHoursCard
              isEnabled={quietHours.data.data.isEnabled}
              startMinute={quietHours.data.data.startMinute}
              endMinute={quietHours.data.data.endMinute}
              savedTimeZone={quietHours.data.data.timeZone}
              onSaved={() => {
                void quietHours.refetch();
              }}
            />
          ) : null}
          {state.phase === "ready" ? (
            <AlertGroupList
              groups={state.groups}
              onSaved={() => {
                void settings.refetch();
              }}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export const Route = createFileRoute("/account_/settings/notifications")({
  beforeLoad: requireSignedIn,
  component: NotificationSettingsScreen,
});
